// The owner's to-dos and next steps in the Home inbox (job R-11, step 3.18, #367; docs/CRM_TOOLS.md).
// The owner's own to-dos and each person's next step, read through crm.read (crm_owner_tasks, R-04's migration
// 20261221040000) and written through crm.write (act group "owner_tasks": add_owner_task, snooze_owner_task,
// complete_owner_task). Days are the business's own (the read's `today`), never the browser's. Never add methods to
// lib/crm.ts.
import type { Crm } from "./crm";
import { plural } from "./format";

export type Bucket = "overdue" | "today" | "upcoming" | "done";
export type Range = "today" | "overdue" | "upcoming" | "open" | "done" | "all";

export interface OwnerTask {
  id: string;
  source: "task" | "next_step";
  title: string;
  notes: string | null;
  due_on: string;
  due_time: string | null;
  due_at: string | null;
  bucket: Bucket;
  days_late: number | null;
  member: string | null;
  person_id: string | null;
  name: string | null;
  organisation_id: string | null;
  firm: string | null;
  deal_id: string | null;
  snoozed_until: string | null;
  snoozes: number;
  status: string;
  completed_at: string | null;
  created_by: string | null;
  created_at: string | null;
}

export interface OwnerTaskCounts {
  overdue: number;
  today: number;
  upcoming: number;
  next_7_days: number;
  approvals: number;
  approvals_oldest: string | null;
}

export interface OwnerTaskList {
  today: string;
  timezone: string;
  member: string | null;
  range: Range;
  tasks: OwnerTask[];
  counts: OwnerTaskCounts;
}

/** What a task is about, when it is added from a record (R-10's header passes this). */
export interface TaskAbout {
  person_id?: string;
  organisation_id?: string;
  deal_id?: string;
  /** "Dave Smith" or "Clearflow Ltd", for the dialog's "About …" line. */
  label?: string;
}

export interface NewTask extends TaskAbout {
  title: string;
  notes?: string;
  due_on?: string;
  due_time?: string;
  member?: string;
}

/** One member's open and recently done tasks (null member: everyone's), as crm_owner_tasks returns them. */
export function ownerTasks(crm: Crm, member: string | null, range: Range = "all"): Promise<OwnerTaskList> {
  return crm.read<OwnerTaskList>("crm_owner_tasks", { p_member: member, p_range: range });
}

export function addOwnerTask(crm: Crm, t: NewTask): Promise<{ task_id: string; due_on: string; member: string }> {
  const params: Record<string, string> = { title: t.title.trim() };
  if (t.notes?.trim()) params.notes = t.notes.trim();
  if (t.due_on) params.due_on = t.due_on;
  if (t.due_time) params.due_time = t.due_time;
  if (t.member) params.member = t.member;
  for (const k of ["person_id", "organisation_id", "deal_id"] as const) if (t[k]) params[k] = t[k]!;
  return crm.write("add_owner_task", params);
}

export function snoozeOwnerTask(crm: Crm, task: string, until: string): Promise<{ task_id: string; due_on: string; snoozes: number }> {
  return crm.write("snooze_owner_task", { task, until });
}

export function completeOwnerTask(crm: Crm, task: string, outcome: "done" | "cancelled" | "open" = "done"): Promise<{ task_id: string; status: string; changed: boolean }> {
  return crm.write("complete_owner_task", { task, outcome });
}

// ------------------------------------------------------------------ days (YYYY-MM-DD, the business's own)
const parseDay = (day: string) => new Date(`${day}T12:00:00Z`);

export function addDays(day: string, n: number): string {
  const d = parseDay(day);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** "Next week" is the coming Monday (a week today when today is a Monday), as Close and Todoist do it. */
export function nextWeek(today: string): string {
  const dow = parseDay(today).getUTCDay(); // 0 Sunday … 6 Saturday
  return addDays(today, dow === 1 ? 7 : (8 - dow) % 7 || 7);
}

/** "Mon 26 Oct". */
export function shortDay(day: string): string {
  const d = parseDay(day);
  if (Number.isNaN(d.getTime())) return day;
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(d).replace(",", "");
}

/** "today", "tomorrow", "yesterday", "Mon 26 Oct", relative to the business's today. */
export function dayLabel(day: string, today: string): string {
  if (day === today) return "today";
  if (day === addDays(today, 1)) return "tomorrow";
  if (day === addDays(today, -1)) return "yesterday";
  return shortDay(day);
}

/** The one line under a task's title: who, when, how late, snoozed. */
export function taskMeta(t: OwnerTask, today: string, showMember = false): string {
  const parts: string[] = [];
  const who = [t.name, t.firm].filter(Boolean).join(", ");
  if (who) parts.push(who);
  if (t.bucket === "done") {
    parts.push(t.status === "cancelled" ? "removed" : "done");
  } else if (t.bucket === "overdue") {
    const n = t.days_late ?? 0;
    parts.push(`due ${shortDay(t.due_on)}, ${n} ${plural(n, "day")} late`);
  } else if (t.bucket === "today") {
    parts.push(t.due_time ? `today at ${t.due_time}` : "today");
  } else {
    parts.push(`${dayLabel(t.due_on, today)}${t.due_time ? ` at ${t.due_time}` : ""}`);
  }
  if (t.snoozes > 0) parts.push(`snoozed ${t.snoozes === 1 ? "once" : `${t.snoozes} times`}`);
  if (showMember && t.member) parts.push(t.member);
  return parts.join(" · ");
}

// ------------------------------------------------------------------ the morning digest (crm_tasks settings section)
export const DIGEST_CHANNEL_LABELS: Record<string, string> = { email: "By email", whatsapp: "On WhatsApp" };

/** "On, 07:30 by email" or "Off". */
export function digestLine(s: { digest?: unknown; digest_time?: unknown; digest_channel?: unknown }): string {
  if (s.digest !== true) return "Morning list off";
  const how = s.digest_channel === "whatsapp" ? "on WhatsApp" : "by email";
  return `Morning list at ${String(s.digest_time || "07:30")} ${how}`;
}
